import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  CalendarClockIcon, ScaleIcon, PrinterIcon, ClipboardCheckIcon, LogInIcon, LogOutIcon,
  TruckIcon, PackageOpenIcon, ScanLineIcon, CheckCircle2Icon, XCircleIcon, AlertTriangleIcon,
  ChevronDownIcon, ChevronRightIcon, MoreVerticalIcon, WarehouseIcon, RefreshCwIcon,
} from "lucide-react";

/* ============ TELA DO OPERADOR: AGENDAMENTO DE DOCA + BALANÇA ============ */

const STATUS_CORES = {
  AGENDADO: "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/40",
  CHEGOU: "bg-[#FFD60A]/10 text-[#FFD60A] border-[#FFD60A]/40",
  EM_CONFERENCIA: "bg-[#FF9F0A]/10 text-[#FF9F0A] border-[#FF9F0A]/40",
  CHECKIN: "bg-[#FF9F0A]/10 text-[#FF9F0A] border-[#FF9F0A]/40",
  FINALIZADO: "bg-[#34C759]/10 text-[#34C759] border-[#34C759]/40",
  CANCELADO: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40",
  NO_SHOW: "bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/40",
};
const STATUS_MANUAIS = ["AGENDADO", "CHEGOU", "EM_CONFERENCIA", "FINALIZADO", "CANCELADO", "NO_SHOW"];
const TIPO_OP_LABEL = { CARGA_CHEIA: "Recebimento", RECEBIMENTO: "Recebimento", COLETA: "Coleta", DEVOLUCAO: "Devolução" };
const CONF_CORES = {
  PENDENTE: "text-muted-foreground",
  RECEBIDO: "text-[#34C759]",
  RECUSADO: "text-[#FF3B30]",
  RECUSADO_PARCIAL: "text-[#FF9F0A]",
};
const fmtH = (s) => (s ? new Date(s).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "—");
const inputCls = "mt-1 rounded-none bg-[#0A0A0C] border-border h-10";

export default function AgendamentoDoca() {
  const [ags, setAgs] = useState([]);
  const [devolucoes, setDevolucoes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [docas, setDocas] = useState([]);
  const [filtroData, setFiltroData] = useState("");
  const [aba, setAba] = useState("cheias"); // cheias | vazios

  // Accordion (visão expansível)
  const [expanded, setExpanded] = useState(null);        // id do agendamento expandido
  const [itensCache, setItensCache] = useState({});      // {agendamento_id: itens[]}

  // Modais
  const [pesoModal, setPesoModal] = useState(null); // {ag, tipo: checkin|checkout}
  const [peso, setPeso] = useState("");
  const [confModal, setConfModal] = useState(null); // {ag, itens}
  const [confState, setConfState] = useState({});
  const [devModal, setDevModal] = useState(false);
  const [devForm, setDevForm] = useState({ fornecedor_id: "", motorista: "", placa: "", doca_codigo: "" });
  const [bipModal, setBipModal] = useState(null); // devolução aberta p/ bipagem
  const [bipCode, setBipCode] = useState("");
  const [devPesoModal, setDevPesoModal] = useState(null);

  const load = async () => {
    const params = filtroData ? { params: { data: filtroData } } : {};
    const [a, d, f, dc] = await Promise.all([
      api.get("/logistica/agendamentos", params).catch(() => ({ data: [] })),
      api.get("/logistica/devolucoes").catch(() => ({ data: [] })),
      api.get("/logistica/fornecedores").catch(() => ({ data: [] })),
      api.get("/logistica/docas").catch(() => ({ data: [] })),
    ]);
    setAgs(a.data); setDevolucoes(d.data); setFornecedores(f.data); setDocas(dc.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtroData]);

  const toggleExpand = async (ag) => {
    if (expanded === ag.id) { setExpanded(null); return; }
    setExpanded(ag.id);
    if (!itensCache[ag.id]) {
      try {
        const { data } = await api.get(`/logistica/agendamentos/${ag.id}`);
        setItensCache((c) => ({ ...c, [ag.id]: data.itens }));
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
  };

  const mudarStatus = async (ag, status) => {
    try {
      await api.patch(`/logistica/agendamentos/${ag.id}/status`, { status });
      toast.success(`${ag.numero} → ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const atribuirDoca = async (ag, doca_codigo) => {
    try {
      await api.patch(`/logistica/agendamentos/${ag.id}/doca`, { doca_codigo });
      toast.success(`${ag.numero} → Doca ${doca_codigo}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const registrarChegada = async (ag) => {
    try { await api.post(`/logistica/agendamentos/${ag.id}/chegada`); toast.success(`Chegada registrada — ${ag.numero}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmarPeso = async () => {
    const p = parseFloat(String(peso).replace(",", "."));
    if (!p || p <= 0) return toast.error("Informe o peso em kg");
    try {
      const { ag, tipo } = pesoModal;
      const { data } = await api.post(`/logistica/agendamentos/${ag.id}/${tipo}`, { peso: p });
      if (tipo === "checkin") {
        toast.success(`Check-In registrado — imprima o Espelho de Recebimento`);
      } else {
        toast.success(`Check-Out concluído · Peso líquido: ${data.peso_liquido?.toLocaleString("pt-BR")} kg — imprima o Ticket`);
      }
      setPesoModal(null); setPeso("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const abrirConferencia = async (ag) => {
    try {
      const { data } = await api.get(`/logistica/agendamentos/${ag.id}`);
      const st = {};
      data.itens.forEach((it) => {
        st[it.id] = {
          status: it.status_conferencia === "PENDENTE" ? "RECEBIDO" : it.status_conferencia,
          qtd_recebida: it.qtd_recebida ?? it.qtd ?? "",
          divergencia_obs: it.divergencia_obs || "",
        };
      });
      setConfState(st);
      setConfModal({ ag: data.agendamento, itens: data.itens });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const salvarConferencia = async () => {
    const payload = {
      itens: confModal.itens.map((it) => ({
        item_id: it.id,
        status: confState[it.id].status,
        qtd_recebida: confState[it.id].status === "RECUSADO" ? 0 : parseFloat(String(confState[it.id].qtd_recebida || 0).replace(",", ".")),
        divergencia_obs: confState[it.id].divergencia_obs || null,
      })),
    };
    try {
      await api.post(`/logistica/agendamentos/${confModal.ag.id}/conferencia`, payload);
      toast.success("Conferência apontada — libere o caminhão para o Check-Out na balança");
      setItensCache((c) => { const n = { ...c }; delete n[confModal.ag.id]; return n; });
      setConfModal(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const abrirDevolucao = async () => {
    if (!devForm.fornecedor_id) return toast.error("Selecione o Fornecedor");
    if (!devForm.motorista.trim() || !devForm.placa.trim()) return toast.error("Informe motorista e placa");
    try {
      const { data } = await api.post("/logistica/devolucoes", devForm);
      toast.success(`Check-In de Vazios aberto — ${data.numero}. Bipe os contentores na doca.`);
      setDevModal(false);
      setDevForm({ fornecedor_id: "", motorista: "", placa: "", doca_codigo: "" });
      setAba("vazios");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const biparVazio = async (e) => {
    e.preventDefault();
    const c = bipCode.trim();
    if (!c) return;
    setBipCode("");
    try {
      const { data } = await api.post(`/logistica/devolucoes/${bipModal.id}/bipar`, { contentor: c });
      toast.success(`Contentor ${c.toUpperCase()} bipado (${data.total} na carga)`);
      const d = await api.get("/logistica/devolucoes");
      setDevolucoes(d.data);
      setBipModal(d.data.find((x) => x.id === bipModal.id));
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  const checkoutVazios = async () => {
    const p = parseFloat(String(peso).replace(",", "."));
    if (!p || p <= 0) return toast.error("Informe o peso de saída carregado");
    try {
      await api.post(`/logistica/devolucoes/${devPesoModal.id}/checkout`, { peso: p });
      toast.success("Devolução de vazios finalizada — portal do fornecedor atualizado");
      setDevPesoModal(null); setPeso("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setConf = (id, patch) => setConfState((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const totalDia = useMemo(() => ags.length, [ags]);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="agendamento-doca-page">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1 flex items-center gap-2">
            <CalendarClockIcon className="w-4 h-4" /> Operador Logístico · Balança Rodoviária
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-none">Agendamento de <span className="text-[#FFD60A]">Doca</span></h1>
        </div>
        <div className="ml-auto flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Filtrar por Data</Label>
            <Input type="date" value={filtroData} onChange={(e) => setFiltroData(e.target.value)} data-testid="filtro-data" className="rounded-none bg-[#0A0A0C] border-border h-10 font-mono mt-1" />
          </div>
          <Button onClick={() => setDevModal(true)} data-testid="btn-checkin-vazios" variant="outline" className="rounded-none border-[#34C759] text-[#34C759] hover:bg-[#34C759]/10 font-bold uppercase tracking-wider text-xs h-10">
            <PackageOpenIcon className="w-4 h-4 mr-2" /> Check-In Carregamento de Vazios
          </Button>
        </div>
      </div>

      <div className="flex border border-border w-fit">
        <button onClick={() => setAba("cheias")} data-testid="tab-cheias" className={`px-5 h-10 text-xs font-bold uppercase tracking-wider ${aba === "cheias" ? "bg-[#FFD60A] text-black" : "bg-[#14151A] text-muted-foreground hover:text-white"}`}>
          Cargas Cheias ({totalDia})
        </button>
        <button onClick={() => setAba("vazios")} data-testid="tab-vazios" className={`px-5 h-10 text-xs font-bold uppercase tracking-wider ${aba === "vazios" ? "bg-[#34C759] text-black" : "bg-[#14151A] text-muted-foreground hover:text-white"}`}>
          Devolução de Vazios ({devolucoes.length})
        </button>
      </div>

      {aba === "cheias" && (
        <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto">
          <table className="w-full text-xs" data-testid="tabela-agendamentos">
            <thead>
              <tr className="text-left text-[9px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="px-2 py-2 w-6"></th>
                <th className="px-3 py-2">Data/Hora Planejada</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Nº Agendamento</th>
                <th className="px-3 py-2">Carregamento</th>
                <th className="px-3 py-2">Motorista</th>
                <th className="px-3 py-2">LPNs/Contentores</th>
                <th className="px-3 py-2">Nº Veículo</th>
                <th className="px-3 py-2">Chegada</th>
                <th className="px-3 py-2">Check-In</th>
                <th className="px-3 py-2">Check-Out</th>
                <th className="px-3 py-2">Tipo Operação</th>
                <th className="px-3 py-2">Doca Pref.</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {ags.length === 0 && (
                <tr><td colSpan={14} className="px-4 py-10 text-center text-muted-foreground">Nenhum agendamento{filtroData ? " para esta data" : ""}. Os fornecedores agendam pelo portal deles.</td></tr>
              )}
              {ags.map((a) => (
                <React.Fragment key={a.id}>
                  <tr onClick={() => toggleExpand(a)} className={`border-b border-border/50 cursor-pointer ${expanded === a.id ? "bg-[#0A0A0C]" : "hover:bg-[#0A0A0C]"}`} data-testid={`ag-row-${a.numero}`}>
                    <td className="px-2 py-2 w-6">
                      {expanded === a.id ? <ChevronDownIcon className="w-4 h-4 text-[#FFD60A]" /> : <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />}
                    </td>
                    <td className="px-3 py-2 font-mono">{a.data_planejada?.split("-").reverse().join("/")} {a.hora_planejada}</td>
                    <td className="px-3 py-2"><Badge className={`rounded-none border text-[9px] font-bold ${STATUS_CORES[a.status] || ""}`}>{a.status.replace("_", " ")}</Badge></td>
                    <td className="px-3 py-2 font-mono text-[#FFD60A] font-bold">{a.numero}</td>
                    <td className="px-3 py-2">{a.fornecedor_nome}{a.operador_nome && <div className="text-[9px] text-muted-foreground">por {a.operador_nome}</div>}</td>
                    <td className="px-3 py-2">{a.motorista}</td>
                    <td className="px-3 py-2 font-mono text-center">{a.total_itens}</td>
                    <td className="px-3 py-2 font-mono">{a.placa}</td>
                    <td className="px-3 py-2 font-mono text-[#00E5FF]">{fmtH(a.hora_chegada)}</td>
                    <td className="px-3 py-2 font-mono text-[#FF9F0A]">{fmtH(a.hora_checkin)}{a.peso_inicial ? <div className="text-[9px] text-muted-foreground">{a.peso_inicial.toLocaleString("pt-BR")} kg</div> : null}</td>
                    <td className="px-3 py-2 font-mono text-[#34C759]">{fmtH(a.hora_checkout)}{a.peso_liquido != null ? <div className="text-[9px] text-muted-foreground">líq {a.peso_liquido.toLocaleString("pt-BR")} kg</div> : null}</td>
                    <td className="px-3 py-2">{TIPO_OP_LABEL[a.tipo_operacao] || a.tipo_operacao}</td>
                    <td className="px-3 py-2 font-mono">{a.doca_codigo}</td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end flex-wrap items-center">
                        {!a.hora_chegada && (
                          <Button size="sm" onClick={() => registrarChegada(a)} data-testid={`btn-chegada-${a.numero}`} variant="outline" className="rounded-none h-7 text-[9px] font-bold uppercase border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10">Chegou</Button>
                        )}
                        {!a.hora_checkin && (
                          <Button size="sm" onClick={() => { setPesoModal({ ag: a, tipo: "checkin" }); setPeso(""); }} data-testid={`btn-checkin-${a.numero}`} className="rounded-none h-7 text-[9px] font-bold uppercase bg-[#FF9F0A] text-black hover:bg-[#FF9F0A]/85">
                            <LogInIcon className="w-3 h-3 mr-1" /> Check-In
                          </Button>
                        )}
                        {a.hora_checkin && (
                          <a href={`/logistica/print/${a.id}?tipo=espelho`} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="outline" data-testid={`btn-espelho-${a.numero}`} className="rounded-none h-7 text-[9px] font-bold uppercase border-border hover:border-white">
                              <PrinterIcon className="w-3 h-3 mr-1" /> Espelho
                            </Button>
                          </a>
                        )}
                        {a.hora_checkin && !a.hora_checkout && (
                          <>
                            <Button size="sm" onClick={() => abrirConferencia(a)} data-testid={`btn-conferencia-${a.numero}`} variant="outline" className="rounded-none h-7 text-[9px] font-bold uppercase border-[#FFD60A] text-[#FFD60A] hover:bg-[#FFD60A]/10">
                              <ClipboardCheckIcon className="w-3 h-3 mr-1" /> Conferência
                            </Button>
                            <Button size="sm" onClick={() => { setPesoModal({ ag: a, tipo: "checkout" }); setPeso(""); }} data-testid={`btn-checkout-${a.numero}`} className="rounded-none h-7 text-[9px] font-bold uppercase bg-[#34C759] text-black hover:bg-[#34C759]/85">
                              <LogOutIcon className="w-3 h-3 mr-1" /> Check-Out
                            </Button>
                          </>
                        )}
                        {a.hora_checkout && (
                          <a href={`/logistica/print/${a.id}?tipo=ticket`} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="outline" data-testid={`btn-ticket-${a.numero}`} className="rounded-none h-7 text-[9px] font-bold uppercase border-[#34C759] text-[#34C759] hover:bg-[#34C759]/10">
                              <PrinterIcon className="w-3 h-3 mr-1" /> Ticket
                            </Button>
                          </a>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" data-testid={`btn-menu-${a.numero}`} className="rounded-none h-7 w-7 p-0 text-muted-foreground hover:text-white">
                              <MoreVerticalIcon className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-[#14151A] border-border rounded-none">
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">{a.numero}</DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-border" />
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger data-testid={`menu-status-${a.numero}`} className="text-xs">
                                <RefreshCwIcon className="w-3.5 h-3.5 mr-2" /> Mudar Status
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent className="bg-[#14151A] border-border rounded-none">
                                {STATUS_MANUAIS.filter((s) => s !== a.status).map((s) => (
                                  <DropdownMenuItem key={s} onClick={() => mudarStatus(a, s)} data-testid={`status-opt-${a.numero}-${s}`} className="text-xs">
                                    <Badge className={`rounded-none border text-[9px] font-bold mr-2 ${STATUS_CORES[s] || ""}`}>{s.replace("_", " ")}</Badge>
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger data-testid={`menu-doca-${a.numero}`} className="text-xs">
                                <WarehouseIcon className="w-3.5 h-3.5 mr-2" /> Atribuir Doca
                              </DropdownMenuSubTrigger>
                              <DropdownMenuSubContent className="bg-[#14151A] border-border rounded-none">
                                {docas.filter((d) => !d.bloqueada).map((d) => (
                                  <DropdownMenuItem key={d.id} onClick={() => atribuirDoca(a, d.codigo)} data-testid={`doca-opt-${a.numero}-${d.codigo}`} className="text-xs font-mono">
                                    {d.codigo} {d.codigo === a.doca_codigo && "✓"}
                                  </DropdownMenuItem>
                                ))}
                                {docas.filter((d) => !d.bloqueada).length === 0 && (
                                  <DropdownMenuItem disabled className="text-xs text-muted-foreground">Nenhuma doca liberada</DropdownMenuItem>
                                )}
                              </DropdownMenuSubContent>
                            </DropdownMenuSub>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                  {expanded === a.id && (
                    <tr className="border-b border-[#FFD60A]/30 bg-[#0A0A0C]" data-testid={`ag-detalhe-${a.numero}`}>
                      <td colSpan={14} className="px-4 py-4">
                        <div className="border border-[#FFD60A]/25 bg-[#14151A]">
                          <div className="px-4 py-2 border-b border-border flex flex-wrap items-center gap-4 text-[10px] uppercase tracking-wider">
                            <span className="text-[#FFD60A] font-bold">Relação da Carga · {a.numero}</span>
                            <span className="text-muted-foreground">CPF Motorista: <b className="font-mono text-white">{a.cpf || "—"}</b></span>
                            <span className="text-muted-foreground">Operador: <b className="text-white">{a.operador_nome || "—"}</b></span>
                            <span className="text-muted-foreground">Contentores: <b className="font-mono text-white">{a.total_itens}</b></span>
                          </div>
                          {!itensCache[a.id] ? (
                            <div className="p-6 text-center text-xs text-muted-foreground animate-pulse">Carregando itens...</div>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-[11px]">
                                <thead>
                                  <tr className="text-left text-[9px] uppercase tracking-widest text-muted-foreground border-b border-border">
                                    <th className="px-3 py-1.5">Contentor / LPN</th>
                                    <th className="px-3 py-1.5">Pedido</th>
                                    <th className="px-3 py-1.5">Material / Produto</th>
                                    <th className="px-3 py-1.5">Qtd</th>
                                    <th className="px-3 py-1.5">Lote</th>
                                    <th className="px-3 py-1.5">Validade</th>
                                    <th className="px-3 py-1.5">Lacres</th>
                                    <th className="px-3 py-1.5">NF Produto</th>
                                    <th className="px-3 py-1.5">NF Contentor</th>
                                    <th className="px-3 py-1.5">Conta e Ordem</th>
                                    <th className="px-3 py-1.5">Conferência</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {itensCache[a.id].map((it) => (
                                    <tr key={it.id} className="border-b border-border/40" data-testid={`item-row-${it.contentor}`}>
                                      <td className="px-3 py-1.5 font-mono font-bold text-[#00E5FF]">{it.contentor}</td>
                                      <td className="px-3 py-1.5 font-mono">{it.pedido || "—"}</td>
                                      <td className="px-3 py-1.5">{it.produto || "—"}</td>
                                      <td className="px-3 py-1.5 font-mono">{it.qtd || "—"}{it.qtd_recebida != null && String(it.qtd_recebida) !== String(it.qtd) ? <span className="text-[#FF9F0A]"> (rec. {it.qtd_recebida})</span> : null}</td>
                                      <td className="px-3 py-1.5 font-mono">{it.lote || "—"}</td>
                                      <td className="px-3 py-1.5 font-mono">{it.validade_lote || "—"}</td>
                                      <td className="px-3 py-1.5 font-mono">{it.lacres || "—"}</td>
                                      <td className="px-3 py-1.5 font-mono">{it.nf_produto || "—"}</td>
                                      <td className="px-3 py-1.5 font-mono">{it.nf_contentor || "—"}</td>
                                      <td className="px-3 py-1.5 font-mono">{it.conta_ordem || "—"}</td>
                                      <td className={`px-3 py-1.5 font-bold text-[10px] uppercase ${CONF_CORES[it.status_conferencia] || ""}`}>
                                        {(it.status_conferencia || "PENDENTE").replace("_", " ")}
                                        {it.divergencia_obs && <div className="text-[9px] font-normal normal-case text-muted-foreground">{it.divergencia_obs}</div>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {aba === "vazios" && (
        <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto">
          <table className="w-full text-xs" data-testid="tabela-devolucoes">
            <thead>
              <tr className="text-left text-[9px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Nº</th>
                <th className="px-3 py-2">Fornecedor</th>
                <th className="px-3 py-2">Motorista / Placa</th>
                <th className="px-3 py-2">Contentores Bipados</th>
                <th className="px-3 py-2">Peso Saída</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {devolucoes.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Nenhuma devolução de vazios. Use "Check-In Carregamento de Vazios".</td></tr>
              )}
              {devolucoes.map((d) => (
                <tr key={d.id} className="border-b border-border/50 hover:bg-[#0A0A0C]" data-testid={`dev-row-${d.numero}`}>
                  <td className="px-3 py-2 font-mono text-[#FFD60A] font-bold">{d.numero}</td>
                  <td className="px-3 py-2">{d.fornecedor_nome}</td>
                  <td className="px-3 py-2">{d.motorista} · <span className="font-mono">{d.placa}</span></td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1 max-w-[400px]">
                      {(d.contentores || []).map((c) => <span key={c} className="font-mono text-[9px] bg-[#34C759]/10 text-[#34C759] border border-[#34C759]/30 px-1 py-0.5">{c}</span>)}
                      {!(d.contentores || []).length && <span className="text-muted-foreground">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono">{d.peso_saida ? `${d.peso_saida.toLocaleString("pt-BR")} kg` : "—"}</td>
                  <td className="px-3 py-2"><Badge className={`rounded-none border text-[9px] font-bold ${STATUS_CORES[d.status] || ""}`}>{d.status}</Badge></td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end">
                      {d.status === "CHECKIN" && (
                        <>
                          <Button size="sm" onClick={() => setBipModal(d)} data-testid={`btn-bipar-${d.numero}`} variant="outline" className="rounded-none h-7 text-[9px] font-bold uppercase border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10">
                            <ScanLineIcon className="w-3 h-3 mr-1" /> Bipar
                          </Button>
                          <Button size="sm" onClick={() => { setDevPesoModal(d); setPeso(""); }} data-testid={`btn-checkout-dev-${d.numero}`} className="rounded-none h-7 text-[9px] font-bold uppercase bg-[#34C759] text-black hover:bg-[#34C759]/85">
                            <ScaleIcon className="w-3 h-3 mr-1" /> Pesar & Finalizar
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* MODAL PESO (checkin/checkout carga cheia) */}
      <Dialog open={!!pesoModal} onOpenChange={(v) => !v && setPesoModal(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-md" data-testid="modal-peso">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
              <ScaleIcon className="w-5 h-5 text-[#FF9F0A]" />
              {pesoModal?.tipo === "checkin" ? "Check-In · Peso Inicial (Veículo Cheio)" : "Check-Out · Peso Final (Veículo Vazio)"}
            </DialogTitle>
          </DialogHeader>
          {pesoModal && (
            <div className="space-y-3">
              <div className="border border-border bg-[#0A0A0C] p-3 text-sm">
                <b className="font-mono text-[#FFD60A]">{pesoModal.ag.numero}</b> · {pesoModal.ag.fornecedor_nome}<br />
                <span className="text-xs text-muted-foreground">{pesoModal.ag.motorista} · {pesoModal.ag.placa} · Doca {pesoModal.ag.doca_codigo}</span>
                {pesoModal.tipo === "checkout" && pesoModal.ag.peso_inicial && (
                  <div className="text-xs font-mono text-[#FF9F0A] mt-1">Peso inicial: {pesoModal.ag.peso_inicial.toLocaleString("pt-BR")} kg</div>
                )}
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Peso na Balança (kg)</Label>
                <Input autoFocus type="number" step="0.01" value={peso} onChange={(e) => setPeso(e.target.value)} data-testid="input-peso"
                       onKeyDown={(e) => e.key === "Enter" && confirmarPeso()}
                       className="mt-1 rounded-none bg-[#0A0A0C] border-[#FF9F0A]/50 h-14 font-mono text-2xl text-center" placeholder={pesoModal.tipo === "checkin" ? "28750" : "16875"} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={confirmarPeso} data-testid="confirmar-peso" className="rounded-none bg-[#FF9F0A] text-black hover:bg-[#FF9F0A]/85 font-bold uppercase tracking-wider w-full h-11">
              {pesoModal?.tipo === "checkin" ? "Registrar Check-In" : "Registrar Check-Out"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL CONFERÊNCIA */}
      <Dialog open={!!confModal} onOpenChange={(v) => !v && setConfModal(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="modal-conferencia">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
              <ClipboardCheckIcon className="w-5 h-5 text-[#FFD60A]" /> Conferência Física na Doca · {confModal?.ag.numero}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {confModal?.itens.map((it) => {
              const st = confState[it.id] || {};
              return (
                <div key={it.id} className={`border p-3 space-y-2 ${st.status === "RECEBIDO" ? "border-[#34C759]/40" : st.status === "RECUSADO" ? "border-[#FF3B30]/40" : "border-[#FF9F0A]/40"}`} data-testid={`conf-item-${it.contentor}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono font-bold text-[#00E5FF]">{it.contentor}</span>
                    <span className="text-sm">{it.produto}</span>
                    <span className="font-mono text-xs text-muted-foreground">Qtd: {it.qtd} · Lote: {it.lote || "—"} · Lacres: {it.lacres || "—"} · NF: {it.nf_produto || "—"}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="flex border border-border">
                      {[["RECEBIDO", CheckCircle2Icon, "#34C759"], ["RECUSADO_PARCIAL", AlertTriangleIcon, "#FF9F0A"], ["RECUSADO", XCircleIcon, "#FF3B30"]].map(([v, Icon, cor]) => (
                        <button key={v} onClick={() => setConf(it.id, { status: v })} data-testid={`conf-${it.contentor}-${v}`}
                                className={`px-3 h-9 text-[10px] font-bold uppercase flex items-center gap-1 ${st.status === v ? "text-black" : "text-muted-foreground hover:text-white"}`}
                                style={st.status === v ? { backgroundColor: cor } : {}}>
                          <Icon className="w-3 h-3" /> {v.replace("_", " ")}
                        </button>
                      ))}
                    </div>
                    {st.status === "RECUSADO_PARCIAL" && (
                      <div>
                        <Label className="text-[9px] uppercase text-[#FF9F0A]">Qtd Recebida</Label>
                        <Input type="number" value={st.qtd_recebida} onChange={(e) => setConf(it.id, { qtd_recebida: e.target.value })} data-testid={`conf-qtd-${it.contentor}`} className="rounded-none bg-[#0A0A0C] border-[#FF9F0A]/50 h-9 font-mono w-28 mt-0.5" />
                      </div>
                    )}
                    {st.status !== "RECEBIDO" && (
                      <div className="flex-1 min-w-[200px]">
                        <Label className="text-[9px] uppercase text-muted-foreground">Divergência / Motivo</Label>
                        <Input value={st.divergencia_obs} onChange={(e) => setConf(it.id, { divergencia_obs: e.target.value })} data-testid={`conf-obs-${it.contentor}`} className="rounded-none bg-[#0A0A0C] border-border h-9 mt-0.5" placeholder="Ex: lacre violado, avaria, etc." />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={salvarConferencia} data-testid="salvar-conferencia" className="rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] font-bold uppercase tracking-wider">
              Salvar Apontamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL ABRIR DEVOLUÇÃO DE VAZIOS */}
      <Dialog open={devModal} onOpenChange={setDevModal}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-md" data-testid="modal-devolucao">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
              <PackageOpenIcon className="w-5 h-5 text-[#34C759]" /> Check-In · Carregamento de Vazios
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Fornecedor</Label>
              <Select value={devForm.fornecedor_id} onValueChange={(v) => setDevForm({ ...devForm, fornecedor_id: v })}>
                <SelectTrigger data-testid="dev-fornecedor" className="mt-1 rounded-none bg-[#0A0A0C] h-10 border-border"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent className="bg-[#14151A] border-border rounded-none">
                  {fornecedores.map((f) => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Motorista</Label>
                <Input data-testid="dev-motorista" value={devForm.motorista} onChange={(e) => setDevForm({ ...devForm, motorista: e.target.value })} className={inputCls} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Placa</Label>
                <Input data-testid="dev-placa" value={devForm.placa} onChange={(e) => setDevForm({ ...devForm, placa: e.target.value.toUpperCase() })} className={`${inputCls} font-mono`} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={abrirDevolucao} data-testid="dev-abrir" className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider w-full">
              Abrir Carregamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL BIPAGEM DE VAZIOS */}
      <Dialog open={!!bipModal} onOpenChange={(v) => !v && setBipModal(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-md" data-testid="modal-bipagem">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
              <ScanLineIcon className="w-5 h-5 text-[#00E5FF]" /> Bipar Contentores Vazios · {bipModal?.numero}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={biparVazio}>
            <Input autoFocus value={bipCode} onChange={(e) => setBipCode(e.target.value)} data-testid="bip-input"
                   className="rounded-none bg-[#0A0A0C] border-[#00E5FF]/50 h-12 font-mono text-lg" placeholder="Bipe ou digite + Enter" />
          </form>
          <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
            {(bipModal?.contentores || []).map((c) => (
              <span key={c} className="font-mono text-[10px] bg-[#34C759]/10 text-[#34C759] border border-[#34C759]/30 px-1.5 py-0.5">{c}</span>
            ))}
          </div>
          <div className="text-xs text-muted-foreground font-mono">{(bipModal?.contentores || []).length} contentor(es) na carga</div>
        </DialogContent>
      </Dialog>

      {/* MODAL PESO SAÍDA VAZIOS */}
      <Dialog open={!!devPesoModal} onOpenChange={(v) => !v && setDevPesoModal(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-md" data-testid="modal-peso-vazios">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2">
              <ScaleIcon className="w-5 h-5 text-[#34C759]" /> Peso de Saída Carregado · {devPesoModal?.numero}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-muted-foreground">{(devPesoModal?.contentores || []).length} contentor(es) vazio(s) carregado(s) para {devPesoModal?.fornecedor_nome}</div>
          <Input autoFocus type="number" step="0.01" value={peso} onChange={(e) => setPeso(e.target.value)} data-testid="input-peso-vazios"
                 onKeyDown={(e) => e.key === "Enter" && checkoutVazios()}
                 className="rounded-none bg-[#0A0A0C] border-[#34C759]/50 h-14 font-mono text-2xl text-center" placeholder="9500" />
          <DialogFooter>
            <Button onClick={checkoutVazios} data-testid="confirmar-peso-vazios" className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider w-full h-11">
              Travar Peso & Finalizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
